import { create } from '@bufbuild/protobuf'
import { Code, ConnectError } from '@connectrpc/connect'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type {
  BiddingBlockApprovalRequest,
  BiddingBlockBid,
  BiddingBlockBidInput,
} from '@ebalance/proto/bffuser/v1/bidding_block_model_pb'
import {
  BiddingBlockBidGroupType,
  BiddingBlockBidInputSchema,
  BiddingBlockBidTimeSlotInputSchema,
  BlockBidWriteBlockedReason,
  BlockBidWriteBlockedReasonSchema,
} from '@ebalance/proto/bffuser/v1/bidding_block_model_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import { DecimalSchema } from '@ebalance/proto/google/type/decimal_pb'
import type { TFunction } from 'i18next'

import {
  type AppErrorInfoReasonEntry,
  readAppErrorInfo,
  resolveAppErrorInfoMessage,
} from '~/libs/appErrorInfo'
import type { FeeAutoConsiderConfirmed } from '~/models/feeAutoConsiderPreview'
import { volumeTotalLimitMessage } from '~/models/volumeTotalLimitMessage'
import { ApprovalFlowUnresolvedError } from '~/pages/SpotBidding/models/approvalModeResolve'
import type {
  BlockBidCreateFormInput,
  BlockLegFormInput,
} from '~/pages/SpotBidding/models/blockBidCreateSchema'
import { effectiveLegSlotVolumes } from '~/pages/SpotBidding/models/blockBidCreateSchema'
import { priceInputOf } from '~/pages/SpotBidding/models/spotBidWrite'

const KIND_TO_GROUP_TYPE: Record<
  'STANDARD' | 'LINK' | 'LOOP',
  BiddingBlockBidGroupType
> = {
  STANDARD: BiddingBlockBidGroupType.BLOCK,
  LINK: BiddingBlockBidGroupType.LINK_BLOCK,
  LOOP: BiddingBlockBidGroupType.LOOP_BLOCK,
}

/** groupType → kind の逆変換 (編集 modal の populate 用)。 */
const GROUP_TYPE_TO_KIND: Record<
  BiddingBlockBidGroupType,
  'STANDARD' | 'LINK' | 'LOOP'
> = {
  [BiddingBlockBidGroupType.UNSPECIFIED]: 'STANDARD',
  [BiddingBlockBidGroupType.BLOCK]: 'STANDARD',
  [BiddingBlockBidGroupType.LINK_BLOCK]: 'LINK',
  [BiddingBlockBidGroupType.LOOP_BLOCK]: 'LOOP',
}

/** ブロック単位で 1 つの入力 (両 Leg に同じ値を載せる。 AC-BSP-031/032)。 */
type BlockSharedMemo = Pick<BlockBidCreateFormInput, 'memoJepx' | 'memoFree'>

/**
 * モーダルが確定したブロック入札。 `feeAutoConsider` は Leg ごとにプレビューで見ていた手数料自動考慮の状態で、
 * ある Leg は価格欄の値を希望価格として送り backend が入札価格を決める (AC-BSP-004b)。 無い Leg (手数料単価を
 * 使えず OFF として扱う間) は価格欄の値を入札価格として送る。
 */
export type BlockBidSubmission = BlockBidCreateFormInput & {
  feeAutoConsider?: {
    primaryLeg?: FeeAutoConsiderConfirmed
    secondaryLeg?: FeeAutoConsiderConfirmed
  }
}

function legToBlockBidInput(
  leg: BlockLegFormInput,
  memo: BlockSharedMemo,
  deliveryDate: ProtoDate,
  fee: FeeAutoConsiderConfirmed | undefined,
): BiddingBlockBidInput {
  // 開始コマ〜終了コマを 1 コマずつ展開し、 コマ別上書き (無ければ全コマ一括値) を
  // 各コマに割り当てる (AC-BSP-030a)。 コマ範囲外の上書きは form 上は保持されるが
  // ここでは採用しない (= 提出値は選択中のコマ範囲のみ)。
  // 全コマ一括値は範囲内の全コマが上書き済みなら空を許す。 空で上書きの無いコマは
  // schema の baseVolumeCheck が弾くので、 schema を通った入力 (= submit handler に
  // 届く form) では各コマの実効値が必ず定まり、 下の throw には到達しない (防御のみ)。
  const timeSlots = effectiveLegSlotVolumes(leg).map(
    ({ timeCode, volumeMwh }) => {
      if (volumeMwh === undefined) {
        throw new Error(`block bid volume is unresolved for slot ${timeCode}`)
      }
      return create(BiddingBlockBidTimeSlotInputSchema, {
        timeCode,
        volumeMwh: create(DecimalSchema, { value: String(volumeMwh) }),
      })
    },
  )
  return create(BiddingBlockBidInputSchema, {
    deliveryDate,
    areaCode: leg.areaCode as AreaCode,
    direction: leg.direction as Side,
    // ブロックに成行は無い (価格は常に指値)。
    ...priceInputOf(false, String(leg.priceYenPerKwh), fee),
    timeSlots,
    ...(memo.memoJepx !== undefined && memo.memoJepx !== ''
      ? { memoJepx: memo.memoJepx }
      : {}),
    ...(memo.memoFree !== undefined && memo.memoFree !== ''
      ? { memoFree: memo.memoFree }
      : {}),
  })
}

/**
 * ブロック入札作成フォーム入力を (groupType, primary, secondary) に変換する。
 *
 * - STANDARD: primary のみ
 * - LINK / LOOP: primary + secondary
 *
 * leg 種別 (blockBidType) は送らない — backend が group_type と primary/secondary の
 * どちらに入れたかから導出する (proto の BiddingBlockBidInput コメント参照)。
 *
 * JEPX自由記述欄・メモはブロック単位で 1 つなので、 両 leg に同じ値を載せる
 * (AC-BSP-031/032)。
 *
 * 各 leg の時間帯 (timeCodeFrom〜timeCodeTo) は 1 コマずつ展開して timeSlots に格納する。
 * 入札量はコマ毎に異なる値を取り得る (AC-BSP-030a。 [[legToBlockBidInput]] 参照)。
 */
export function formToCreateBlockInput(
  form: BlockBidSubmission,
  deliveryDate: ProtoDate,
): {
  groupType: BiddingBlockBidGroupType
  primary: BiddingBlockBidInput
  secondary: BiddingBlockBidInput | undefined
} {
  const groupType = KIND_TO_GROUP_TYPE[form.kind]
  const memo = { memoJepx: form.memoJepx, memoFree: form.memoFree }
  const primary = legToBlockBidInput(
    form.primaryLeg,
    memo,
    deliveryDate,
    form.feeAutoConsider?.primaryLeg,
  )
  const secondary =
    form.kind !== 'STANDARD' && form.secondaryLeg !== undefined
      ? legToBlockBidInput(
          form.secondaryLeg,
          memo,
          deliveryDate,
          form.feeAutoConsider?.secondaryLeg,
        )
      : undefined
  return { groupType, primary, secondary }
}

/**
 * ブロック入札編集フォーム入力を UpdateBiddingBlockApprovalRequestRequest の
 * primary / secondary に変換する。
 *
 * UpdateBiddingBlockApprovalRequestRequest には group_type field が無い (= 編集で種別は
 * 変更不可) ため、 formToCreateBlockInput の leg 部分のみを再利用する。
 */
export function formToUpdateBlockInput(
  form: BlockBidSubmission,
  deliveryDate: ProtoDate,
): {
  primary: BiddingBlockBidInput
  secondary: BiddingBlockBidInput | undefined
} {
  const { primary, secondary } = formToCreateBlockInput(form, deliveryDate)
  return { primary, secondary }
}

/** timeSlots の timeCode 配列から開始/終了コマ (min/max) を求める。 */
function timeCodeRangeOf(bid: BiddingBlockBid): {
  timeCodeFrom: number
  timeCodeTo: number
} {
  const codes = bid.timeSlots.map((s) => s.timeCode)
  if (codes.length === 0) return { timeCodeFrom: 1, timeCodeTo: 1 }
  const sorted = [...codes].sort((a, b) => a - b)
  return {
    timeCodeFrom: sorted[0] ?? 1,
    timeCodeTo: sorted[sorted.length - 1] ?? 1,
  }
}

/**
 * コマ別入札量の配列を 2 層モデル (全コマ一括値 + 上書き) に畳む。
 *
 * proto は全コマぶんの値を持つので、 そのまま上書きに展開すると編集 modal の
 * 「全コマ一括」欄が意味を失う。 **最頻値**を一括値に採り、 そこから外れるコマだけを
 * 上書きとして残す (= 上書き件数が最小になる = 利用者が入力した形に最も近い)。
 * 同数の場合はコマ番号が小さい方を採る (= 決定的に再現する)。
 */
function foldSlotVolumes(
  volumes: Array<{ timeCode: number; volumeMwh: number }>,
): {
  baseVolumeMwh: number
  slotVolumes: Array<{ timeCode: number; volumeMwh: number }>
} {
  if (volumes.length === 0) return { baseVolumeMwh: 0, slotVolumes: [] }
  const counts = new Map<number, number>()
  for (const v of volumes) {
    counts.set(v.volumeMwh, (counts.get(v.volumeMwh) ?? 0) + 1)
  }
  // volumes はコマ昇順で渡される前提なので、 先に現れた値ほど tie-break で優先される。
  let baseVolumeMwh = volumes[0]?.volumeMwh ?? 0
  let bestCount = 0
  for (const v of volumes) {
    const count = counts.get(v.volumeMwh) ?? 0
    if (count > bestCount) {
      bestCount = count
      baseVolumeMwh = v.volumeMwh
    }
  }
  return {
    baseVolumeMwh,
    slotVolumes: volumes.filter((v) => v.volumeMwh !== baseVolumeMwh),
  }
}

/** BiddingBlockBid (leg) → BlockLegFormInput (編集 modal の populate 用)。 */
function blockBidToLegForm(bid: BiddingBlockBid): BlockLegFormInput {
  const { timeCodeFrom, timeCodeTo } = timeCodeRangeOf(bid)
  // コマ昇順に揃えてから畳む (proto の time_slots 順に依存しない)。
  const volumes = [...bid.timeSlots]
    .sort((a, b) => a.timeCode - b.timeCode)
    .map((slot) => ({
      timeCode: slot.timeCode,
      volumeMwh: parseFloat(slot.volumeMwh?.value ?? '0'),
    }))
  const { baseVolumeMwh, slotVolumes } = foldSlotVolumes(volumes)
  return {
    areaCode: bid.areaCode,
    direction: bid.direction,
    volumeMwh: baseVolumeMwh,
    slotVolumes,
    priceYenPerKwh: parseFloat(bid.priceYenPerKwh?.value ?? '0'),
    timeCodeFrom,
    timeCodeTo,
  }
}

/**
 * BiddingBlockApprovalRequest (group) → BlockBidCreateFormInput (編集 modal の populate 用)。
 *
 * group_type → kind、 primary/secondary → primaryLeg/secondaryLeg に逆変換する。
 */
export function blockGroupToForm(
  group: BiddingBlockApprovalRequest,
): BlockBidCreateFormInput {
  const kind = GROUP_TYPE_TO_KIND[group.groupType]
  const primaryBid = group.primary

  const emptyLeg: BlockLegFormInput = {
    areaCode: 0,
    direction: 0,
    volumeMwh: 0,
    slotVolumes: [],
    priceYenPerKwh: 0,
    timeCodeFrom: 1,
    timeCodeTo: 1,
  }
  const primaryLeg =
    primaryBid !== undefined ? blockBidToLegForm(primaryBid) : emptyLeg

  // 発注主体区分はグループの区分をそのまま選択値にする。
  const issuerFields = {
    issuerType: group.issuerType,
  }
  // JEPX自由記述欄・メモはブロック単位で 1 つ (保存時に両 leg へ同じ値を載せる)
  // ので、 primary の値を復元する (AC-BSP-031/032)。
  const memoFields = {
    memoJepx: primaryBid?.memoJepx,
    memoFree: primaryBid?.memoFree,
  }

  if (kind === 'STANDARD') {
    return { kind, primaryLeg, ...issuerFields, ...memoFields }
  }

  const secondaryBid = group.secondary
  return {
    kind,
    primaryLeg,
    secondaryLeg:
      secondaryBid !== undefined ? blockBidToLegForm(secondaryBid) : undefined,
    ...issuerFields,
    ...memoFields,
  }
}

/**
 * 明細 Get が未解決 (loading / 一時失敗) のまま承認申請・JEPX 提出の状態遷移を
 * 実行しようとした時に投げる sentinel (= 通常入札の `SpotDetailUnresolvedError`
 * と同型)。 未解決の group は内容 (leg / 価格 / 数量) を利用者が確認できておらず、
 * そのまま REQUEST/SUBMIT すると中身を把握しないまま不可逆な状態遷移を
 * 実行してしまう。
 */
export class BlockDetailUnresolvedError extends Error {
  constructor() {
    super('block approval request details are not resolved yet')
    this.name = 'BlockDetailUnresolvedError'
  }
}

/**
 * entry は operation-scoped (画面上部の Alert / toast)。 入札制限違反は
 * 「どの leg / どのコマが原因か」が実行時に決まり単一入力欄に 1:1 で
 * 帰属しないため。 手数料自動考慮の 2 理由も、 どの Leg かは返らず、 Leg の価格欄の
 * 2 行文言はプレビューが出すので、 上部の 1 文にする。
 */
const BLOCK_WRITE_BLOCKED_REASON_KEYS: Record<
  BlockBidWriteBlockedReason,
  AppErrorInfoReasonEntry | undefined
> = {
  [BlockBidWriteBlockedReason.UNSPECIFIED]: undefined,
  [BlockBidWriteBlockedReason.BID_LIMIT_VIOLATION]: {
    key: 'page:biddingBlock.serverError.reason.bidLimitViolation',
  },
  [BlockBidWriteBlockedReason.BID_VOLUME_TOTAL_LIMIT_VIOLATION]: {
    key: 'page:biddingBlock.serverError.reason.bidVolumeTotalLimitViolation',
  },
  [BlockBidWriteBlockedReason.GATE_CLOSURE]: {
    key: 'page:biddingBlock.serverError.reason.gateClosure',
  },
  [BlockBidWriteBlockedReason.FEE_AUTO_CONSIDER_CHANGED]: {
    key: 'page:biddingBlock.serverError.reason.feeAutoConsiderChanged',
  },
  [BlockBidWriteBlockedReason.FEE_ADJUSTED_PRICE_OUT_OF_RANGE]: {
    key: 'page:biddingBlock.serverError.reason.feeAdjustedPriceOutOfRange',
  },
  // AC-BSP-117: 「下書きに戻す」 を押した時に送信 worker が送信の最中。 文言は #11944 Q2 の仮採用。
  [BlockBidWriteBlockedReason.BID_SEND_IN_FLIGHT]: {
    key: 'page:biddingBlock.serverError.reason.bidSendInFlight',
  },
}

/**
 * ブロック入札の作成・更新が手数料の理由で拒否されたか。 開いたままのモーダルが突き合わせているプレビューと
 * 設定を取り直す引き金 (通常入札の `isSpotFeePreviewStale` と対。 拒否理由の enum が別)。
 */
export function isBlockFeePreviewStale(err: unknown): boolean {
  if (!(err instanceof ConnectError) || err.code !== Code.FailedPrecondition) {
    return false
  }
  const reason = readAppErrorInfo(err, BlockBidWriteBlockedReasonSchema)?.reason
  return (
    reason === BlockBidWriteBlockedReason.FEE_AUTO_CONSIDER_CHANGED ||
    reason === BlockBidWriteBlockedReason.FEE_ADJUSTED_PRICE_OUT_OF_RANGE
  )
}

/**
 * FailedPrecondition に付いた `google.rpc.ErrorInfo` detail の reason から理由別
 * の文言を引く。 detail が無い / reason が未知 (= backend 先行デプロイ) /
 * registry entry が `undefined` の場合は undefined を返し、 呼び出し元が従来の
 * 汎用文言へフォールバックする。
 * CSV 取込の確定 (`SpotCsvImport`) も同じ文言を使う (#6095)。
 */
const BLOCK_VOLUME_TOTAL_KEYS = {
  detail: 'page:biddingBlock.serverError.reason.bidVolumeTotalLimitViolation',
  plain:
    'page:biddingBlock.serverError.reason.bidVolumeTotalLimitViolationPlain',
}

export function blockWriteBlockedReasonMessage(
  err: ConnectError,
  t: TFunction,
): string | undefined {
  const info = readAppErrorInfo(err, BlockBidWriteBlockedReasonSchema)
  if (
    info?.reason === BlockBidWriteBlockedReason.BID_VOLUME_TOTAL_LIMIT_VIOLATION
  ) {
    return volumeTotalLimitMessage(info.metadata, t, BLOCK_VOLUME_TOTAL_KEYS)
  }
  return resolveAppErrorInfoMessage(
    err,
    BlockBidWriteBlockedReasonSchema,
    BLOCK_WRITE_BLOCKED_REASON_KEYS,
    t,
  )?.message
}

/**
 * 応答に載った `BlockBidWriteBlockedReason` から単件エラーと同じ文言を引く
 * (通常入札の `spotWriteBlockedReasonLabel` と対)。 専用文言を持たない値は undefined。
 */
export function blockWriteBlockedReasonLabel(
  reason: BlockBidWriteBlockedReason,
  t: TFunction,
): string | undefined {
  if (reason === BlockBidWriteBlockedReason.BID_VOLUME_TOTAL_LIMIT_VIOLATION) {
    // まとめて承認の応答はエリア・コマ・合計を持たない。穴の空いた文は出さない。
    return t(BLOCK_VOLUME_TOTAL_KEYS.plain)
  }
  const entry = BLOCK_WRITE_BLOCKED_REASON_KEYS[reason]
  return entry === undefined ? undefined : t(entry.key)
}

/** Create/Transition の ConnectError を画面表示用の日本語文言に訳す (block 用)。 */
export function translateBlockWriteError(err: unknown, t: TFunction): string {
  if (err instanceof BlockDetailUnresolvedError) {
    return t('page:biddingBlock.serverError.detailUnresolved')
  }
  if (err instanceof ApprovalFlowUnresolvedError) {
    return t('page:biddingBlock.serverError.approvalFlowUnavailable')
  }
  if (err instanceof ConnectError) {
    switch (err.code) {
      case Code.Aborted:
        return t('page:biddingBlock.serverError.aborted')
      case Code.AlreadyExists:
        return t('page:biddingBlock.serverError.alreadyExists')
      case Code.NotFound:
        return t('page:biddingBlock.serverError.notFound')
      case Code.InvalidArgument:
        return t('page:biddingBlock.serverError.invalidArgument')
      case Code.PermissionDenied:
        return t('page:biddingBlock.serverError.permissionDenied')
      case Code.FailedPrecondition:
        return (
          blockWriteBlockedReasonMessage(err, t) ??
          t('page:biddingBlock.serverError.failedPrecondition')
        )
      default:
        return t('page:biddingBlock.serverError.unknown')
    }
  }
  return t('page:biddingBlock.serverError.unknown')
}
